import React from 'react'

import { TranslatorProvider as Provider } from '@wicadu/reina-pepiada/hooks'
import { Languages, Regions } from '@wicadu/langscope'

import {
  CONTACT_US_TRANSLATIONS,
  CREATE_ITEMS_TRANSLATIONS,
  CREATE_MANUAL_STORE_ORDERS_TRANSLATIONS,
  CREATE_MANUAL_USER_ORDERS_TRANSLATIONS,
  HOME_TRANSLATIONS,
  HOW_TO_CANCEL_ORDERS_TRANSLATIONS,
  ITEMS_HOME_TRANSLATIONS,
  LEGAL_HOME_TRANSLATIONS,
  LEGAL_PRIVACY_POLICY_TRANSLATIONS,
  LEGAL_TERMS_AND_CONDITIONS_TRANSLATIONS,
  MANAGE_ITEMS_MEDIA_TRANSLATIONS,
  MANAGING_DOCS_FOR_ORDERS_TRANSLATIONS,
  MANAGING_PAYMENTS_FOR_ORDERS_TRANSLATIONS,
  ORDERS_HOME_TRANSLATIONS,
  ORDERS_PAYMENT_METHODS_TRANSLATIONS,
  ORDERS_RETURNS_TRANSLATIONS,
  UPDATE_ITEMS_TRANSLATIONS,
  WRITE_OFF_INVENTORY_TRANSLATIONS,
  PRICING_YOUR_PRODUCTS_TRANSLATIONS,
  LEGAL_REFUND_POLICY_TRANSLATIONS
} from '@translations/pages'
import { PagesId } from '@ts-enums/PagesId'
import {
  LAYOUT_TRANSLATIONS,
  AUTH_CALL_TO_ACTION_TRANSLATIONS,
  INTENDED_AUDIENCE_TRANSLATIONS,
  SUPPORT_SECTION_TRANSLATIONS,
  TOPIC_CARD_TRANSLATIONS,
} from '@translations/ui'
import { UiId } from '@ts-enums/UiId'
import manager from '@utils/loadLangScopeManager'
import { SharedId } from '@ts-enums/SharedId'
import { SEO_TRANSLATIONS } from '@translations/shared/seo'
import { FAQ_QUESTIONS_TRANSLATIONS } from '@translations/ui/faq-questions'
import { SEARCH_ARTICLES_TRANSLATIONS } from '@translations/ui/search-articles'

interface Props {
  children: React.ReactNode
  lang?: Languages
}

const initialTranslations = {
  [`page-${PagesId.Home}`]: HOME_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.ContactUs}`]: CONTACT_US_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.LegalHome}`]: LEGAL_HOME_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.LegalPrivacyPolicy}`]: LEGAL_PRIVACY_POLICY_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.LegalTermsAndConditions}`]: LEGAL_TERMS_AND_CONDITIONS_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.LegalRefundPolicy}`]: LEGAL_REFUND_POLICY_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.OrdersHome}`]: ORDERS_HOME_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.CreateManualUserOrders}`]:
    CREATE_MANUAL_USER_ORDERS_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.CreateManualStoreOrders}`]:
    CREATE_MANUAL_STORE_ORDERS_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.HowToCancelOrders}`]:
    HOW_TO_CANCEL_ORDERS_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.OrdersReturns}`]: ORDERS_RETURNS_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.OrdersPaymentMethods}`]:
    ORDERS_PAYMENT_METHODS_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.ManagingPaymentsForOrders}`]:
    MANAGING_PAYMENTS_FOR_ORDERS_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.ManagingDocsForOrders}`]:
    MANAGING_DOCS_FOR_ORDERS_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.ItemsHome}`]: ITEMS_HOME_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.CreateItems}`]: CREATE_ITEMS_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.UpdateItems}`]: UPDATE_ITEMS_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.ManageItemsMedia}`]: MANAGE_ITEMS_MEDIA_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.WriteOffInventory}`]: WRITE_OFF_INVENTORY_TRANSLATIONS[Regions.Global],
  [`page-${PagesId.PricingYourProducts}`]:
    PRICING_YOUR_PRODUCTS_TRANSLATIONS[Regions.Global],
  
  [`ui-${UiId.Layout}`]: LAYOUT_TRANSLATIONS[Regions.Global],
  [`ui-${UiId.IntendedAudience}`]:
    INTENDED_AUDIENCE_TRANSLATIONS[Regions.Global],
  [`ui-${UiId.AuthCallToAction}`]:
    AUTH_CALL_TO_ACTION_TRANSLATIONS[Regions.Global],
  [`ui-${UiId.TopicCard}`]: TOPIC_CARD_TRANSLATIONS[Regions.Global],
  [`ui-${UiId.SupportSection}`]: SUPPORT_SECTION_TRANSLATIONS[Regions.Global],
  [`ui-${UiId.FaqQuestions}`]: FAQ_QUESTIONS_TRANSLATIONS[Regions.Global],
  [`ui-${UiId.SearchArticles}`]: SEARCH_ARTICLES_TRANSLATIONS[Regions.Global],

  [`shared-${SharedId.Seo}`]: SEO_TRANSLATIONS,
}

const initialCustomTranslations = {}

function TranslatorProvider({ children, lang }: Props) {
  return (
    <Provider
      manager={manager}
      initialLang={lang || Languages.English}
      initialTranslations={initialTranslations as never}
      initialCustomTranslations={initialCustomTranslations as never}
    >
      {children}
    </Provider>
  )
}

export default TranslatorProvider
